import { Animator } from '../../../utils/animator';
import { clamp } from './clamp';
import { MouseRecognizer } from './input-recognition/mouse-recognizer';
import { TouchRecognizer } from './input-recognition/touch-recognizer';
import { addPoints, centerPoint, scalePoint, subtractPoints } from './measurement-utils';
import { getRelativePoint } from './window-utils';
export class ZoomHandler {
    /**
     * Creates a zoom handler and registers required input recognizer callbacks.
     */
    constructor() {
        /**
         * Threshold of the minimum initial scale required for tap to zoom to be enabled.
         */
        this.tapToZoomScaleThreshold = 0.75;
        /**
         * Zoom scale increment used for the zoomIn and zoomOut functions.
         */
        this.zoomIncrement = 1.5;
        /**
         * Pan increment used for panUp, panRight, panDown and panLeft functions.
         */
        this.panIncrement = 100;
        /**
         * Duration of animation of zoom and pan changes. Does not apply to click/tap and drag events.
         */
        this.animationDuration = 100;
        /**
         * Extra padding to allow the user to pan past the zoom buttons.
         */
        this.zoomButtonPadding = 44;
        /**
         * Maximum scale of the content element.
         */
        this.maxScale = 1;
        /**
         * Recognizer for touch events.
         */
        this.touchRecognizer = new TouchRecognizer();
        /**
         * Recognizer for mouse events.
         */
        this.mouseRecognizer = new MouseRecognizer();
        /**
         * Zooms in to the center of the viewport by the zoomIncrement.
         */
        this.zoomIn = () => {
            const transform = this.getZoomTransform(this.zoomIncrement, this.getViewportCenterPoint());
            this.animateContentTransformation(transform);
        };
        /**
         * Zooms out from the center of the viewport by the zoomIncrement.
         */
        this.zoomOut = () => {
            const transform = this.getZoomTransform(1 / this.zoomIncrement, this.getViewportCenterPoint());
            this.animateContentTransformation(transform);
        };
        /**
         * Pans the content to a given x and y position.
         * @returns A boolean indicating whether any pan occurred.
         */
        this.panTo = (point, animate = false) => {
            const targetTranslation = this.restrictTranslation(point, this.getCurrentTotalScale());
            const currentTranslation = this.currentContentTransformation.translation;
            const contentWasPanned = currentTranslation.x !== targetTranslation.x || currentTranslation.y !== targetTranslation.y;
            if (contentWasPanned) {
                const transform = { translation: targetTranslation };
                if (animate)
                    this.animateContentTransformation(transform);
                else
                    this.applyContentTransformation(transform, true);
            }
            return contentWasPanned;
        };
        /**
         * Pans the content by a given translation amount.
         * @returns A boolean indicating whether any pan occurred.
         */
        this.panBy = (translation, animate = false) => {
            const newTranslation = addPoints(this.currentContentTransformation.translation, translation);
            return this.panTo(newTranslation, animate);
        };
        /**
         * Pans the content upwards by the panIncrement.
         */
        this.panUp = () => this.panBy({ x: 0, y: this.panIncrement }, true);
        /**
         * Pans the content downwards by the panIncrement.
         */
        this.panDown = () => this.panBy({ x: 0, y: -this.panIncrement }, true);
        /**
         * Pans the content left by the panIncrement.
         */
        this.panLeft = () => this.panBy({ x: this.panIncrement, y: 0 }, true);
        /**
         * Pans the content right by the panIncrement.
         */
        this.panRight = () => this.panBy({ x: -this.panIncrement, y: 0 }, true);
        /**
         * Zooms by a given scale around a given center point.
         */
        this.handlePinch = (pinchScale, pinchCenter) => {
            const relativePoint = getRelativePoint(pinchCenter, this.viewport);
            const zoomTransform = this.getZoomTransform(pinchScale / this.currentContentTransformation.deltaScale, relativePoint);
            this.applyContentTransformation(zoomTransform, true);
        };
        /**
         * Gets the transformation result of a zoom action.
         */
        this.getZoomTransform = (zoomAmount, center) => {
            const newScale = this.restrictScale(this.getCurrentTotalScale() * zoomAmount);
            return this.getTransformForScale(newScale, center);
        };
        this.getTransformForScale = (newScale, center) => {
            // Get the zoom center point relative to the content
            const contentPoint = subtractPoints(center, this.currentContentTransformation.translation);
            // Work out the size of the content with the new scaling
            const inversePreviousScale = scalePoint(1 / this.getCurrentTotalScale());
            const applyNewScale = scalePoint(newScale);
            const contentPointWithNewScale = applyNewScale(inversePreviousScale(contentPoint));
            // Calculate transforms
            const newTranslation = this.restrictTranslation(subtractPoints(center, contentPointWithNewScale), newScale);
            return {
                scale: newScale,
                translation: newTranslation
            };
        };
        /**
         * Returns the current total content scale.
         */
        this.getCurrentTotalScale = () => this.currentContentTransformation.scale * this.currentContentTransformation.deltaScale;
        /**
         * Restricts a given translation by the translation limits.
         */
        this.restrictTranslation = (translation, scale) => {
            const { minX, minY, maxX, maxY } = this.getTranslationLimits(scale);
            return {
                x: clamp(minX, maxX)(translation.x),
                y: clamp(minY, maxY)(translation.y)
            };
        };
        /**
         * Returns the minimum and maximum x and y translation values.
         */
        this.getTranslationLimits = (scale) => {
            const contentDimensions = this.calculateContentDimensionsAtScale(scale);
            let minX;
            let maxX;
            let minY;
            let maxY;
            if (this.viewportDimensions.width >= contentDimensions.width) {
                minX = (this.viewportDimensions.width - contentDimensions.width) / 2;
                maxX = minX;
            }
            else {
                minX = this.viewportDimensions.width - contentDimensions.width - this.zoomButtonPadding;
                maxX = 0;
            }
            if (this.viewportDimensions.height >= contentDimensions.height) {
                minY = (this.viewportDimensions.height - contentDimensions.height) / 2;
                maxY = minY;
            }
            else {
                minY = this.viewportDimensions.height - contentDimensions.height;
                maxY = 0;
            }
            return {
                minX: minX,
                minY: minY,
                maxX: maxX,
                maxY: maxY
            };
        };
        /**
         * Applies a given transformation to the content element.
         */
        this.applyContentTransformation = (transformation, fastScale) => {
            const contentTransformation = Object.assign({}, this.currentContentTransformation);
            if (transformation.translation) {
                contentTransformation.translation = transformation.translation;
            }
            if (transformation.scale) {
                contentTransformation.scale = fastScale ? this.currentContentTransformation.scale : transformation.scale;
                contentTransformation.deltaScale = fastScale
                    ? transformation.scale / this.currentContentTransformation.scale
                    : 1;
            }
            this.currentContentTransformation = contentTransformation;
            this.updatePanAvailability();
            this.updateZoomAvailability();
            requestAnimationFrame(() => this.setContentStyles(contentTransformation));
        };
        /**
         * Animates a content transformation.
         */
        this.animateContentTransformation = (transform) => {
            if (this.animator)
                this.animator.cancel();
            const startTransformation = {
                translation: this.currentContentTransformation.translation,
                scale: this.getCurrentTotalScale()
            };
            const endTransformation = {
                translation: transform.translation || startTransformation.translation,
                scale: transform.scale || startTransformation.scale
            };
            this.animator = new Animator(startTransformation, endTransformation, this.animationDuration, this.applyAnimationFrameContentTransformation);
            // Start the animation.
            this.animator.start();
        };
        /**
         * Applies the content transformation at a given animation frame.
         */
        this.applyAnimationFrameContentTransformation = (frame) => {
            const { progress, isFinalFrame, startState, endState } = frame;
            if (isFinalFrame) {
                this.applyContentTransformation(frame.endState, false);
                this.animator = undefined;
                return;
            }
            const changeInTranslation = scalePoint(progress)(subtractPoints(endState.translation, startState.translation));
            const changeInScale = (endState.scale - startState.scale) * progress;
            const deltaScale = (startState.scale + changeInScale) / startState.scale;
            const transientTransform = {
                scale: startState.scale,
                deltaScale: deltaScale,
                translation: addPoints(startState.translation, changeInTranslation)
            };
            this.currentContentTransformation = transientTransform;
            this.setContentStyles(transientTransform);
        };
        /**
         * Sets the content styles based on a given transformation.
         */
        this.setContentStyles = (transformation) => {
            const { scale, deltaScale, translation } = transformation;
            const contentDimensions = this.calculateContentDimensionsAtScale(scale);
            this.content.setAttribute('style', `transform: translate3d(${Math.floor(translation.x)}px, ${Math.floor(translation.y)}px, 0) ` +
                `scale3d(${deltaScale}, ${deltaScale}, ${deltaScale}); ` +
                `width: ${Math.floor(contentDimensions.width)}px; ` +
                `height: ${Math.floor(contentDimensions.height)}px;`);
            if (this.onContentTransformationChanged)
                this.onContentTransformationChanged(transformation);
        };
        /**
         * Updates the pan availability based on the current transformations.
         */
        this.updatePanAvailability = () => {
            if (!this.onPanAvailabilityChanged)
                return; // Nobody cares, no need to update
            const { minX, minY, maxX, maxY } = this.getTranslationLimits(this.currentContentTransformation.scale);
            const oldAvailability = this.lastPanAvailability;
            const currentTranslation = this.currentContentTransformation.translation;
            const newAvailability = {
                left: currentTranslation.x < maxX,
                right: currentTranslation.x > minX,
                up: currentTranslation.y < maxY,
                down: currentTranslation.y > minY
            };
            if (oldAvailability &&
                newAvailability.left === oldAvailability.left &&
                newAvailability.right === oldAvailability.right &&
                newAvailability.up === oldAvailability.up &&
                newAvailability.down === oldAvailability.down) {
                return; // Nothing has changed, no need to update
            }
            this.lastPanAvailability = newAvailability;
            this.onPanAvailabilityChanged(newAvailability);
        };
        /**
         * Updates the zoom availability based on the current transformations.
         */
        this.updateZoomAvailability = () => {
            if (!this.onZoomAvailabilityChanged)
                return; // Nobody cares, no need to update
            const oldAvailability = this.lastZoomAvailability;
            const currentScale = this.currentContentTransformation.scale;
            const newAvailability = {
                in: currentScale < this.maxScale,
                out: currentScale > this.minScale
            };
            if (oldAvailability &&
                newAvailability.in === oldAvailability.in &&
                newAvailability.out === oldAvailability.out) {
                return; // Nothing has changed, no need to update
            }
            this.lastZoomAvailability = newAvailability;
            this.onZoomAvailabilityChanged(newAvailability);
        };
        /**
         * Adds the required event listeners to the viewport and document.
         */
        this.setEventListeners = () => {
            this.viewport.addEventListener('touchstart', this.touchRecognizer.start, { passive: false });
            this.viewport.addEventListener('mousedown', this.mouseRecognizer.down, { passive: true });
            this.viewport.addEventListener('click', this.mouseRecognizer.click, { capture: true, passive: false });
            this.viewport.addEventListener('touchmove', this.touchRecognizer.move, { passive: false });
            // These need to target the whole document to allow panning to extend past the viewport.
            document.addEventListener('touchend', this.handleTouchEnd, { passive: false });
            document.addEventListener('touchcancel', this.handleTouchEnd, { passive: false });
            document.addEventListener('mouseup', this.handleMouseUp, { passive: true });
            document.addEventListener('mousemove', this.mouseRecognizer.move, { passive: true });
            // We need to listen to resize events to update the viewport size.
            window.addEventListener('resize', this.handleResize, { passive: true });
        };
        /**
         * Removes the event listeners added by setEventListeners
         */
        this.removeEventListeners = () => {
            if (this.viewport) {
                this.viewport.removeEventListener('touchstart', this.touchRecognizer.start);
                this.viewport.removeEventListener('mousedown', this.mouseRecognizer.down);
                this.viewport.removeEventListener('click', this.mouseRecognizer.click, true);
                this.viewport.removeEventListener('touchmove', this.touchRecognizer.move);
            }
            document.removeEventListener('touchend', this.handleTouchEnd);
            document.removeEventListener('touchcancel', this.handleTouchEnd);
            document.removeEventListener('mouseup', this.handleMouseUp);
            document.removeEventListener('mousemove', this.mouseRecognizer.move);
            window.removeEventListener('resize', this.handleResize);
        };
        /**
         * Common handler for end events.
         */
        this.handleEndEvent = () => {
            if (this.currentContentTransformation.deltaScale !== 1) {
                this.applyContentTransformation({
                    translation: this.currentContentTransformation.translation,
                    scale: this.getCurrentTotalScale()
                }, false);
            }
        };
        /**
         * Handler for touchend events.
         */
        this.handleTouchEnd = event => {
            this.handleEndEvent();
            if (event.target && this.content.contains(event.target)) {
                this.touchRecognizer.end(event);
            }
        };
        /**
         * Handler for mouseup events.
         */
        this.handleMouseUp = (event) => {
            this.handleEndEvent();
            this.mouseRecognizer.up(event);
        };
        /**
         * Handler for tap events.
         */
        this.handleTap = (point, target) => {
            const shouldTapToZoom = this.currentContentTransformation.scale === this.minScale &&
                this.currentContentTransformation.scale < this.tapToZoomScaleThreshold;
            if (shouldTapToZoom) {
                // Zoom in to the maximum scale at the point that was tapped.
                const relativeTapPoint = getRelativePoint(point, this.viewport);
                this.animateContentTransformation(this.getTransformForScale(this.maxScale, relativeTapPoint));
            }
            else {
                // Otherwise, dispatch a click event to replace the one that was swallowed.
                const evt = new MouseEvent('click', {
                    bubbles: true,
                    cancelable: true,
                    view: window
                });
                if (target)
                    target.dispatchEvent(evt);
            }
        };
        /**
         * Handler for double-tap events.
         */
        this.handleDoubleTap = (point) => {
            const transform = this.getZoomTransform(this.zoomIncrement, getRelativePoint(point, this.viewport));
            this.animateContentTransformation(transform);
        };
        /**
         * Handler for window resize events.
         */
        this.handleResize = () => {
            const viewportDimensions = this.getViewportDimensions();
            if (this.viewportDimensions.width !== viewportDimensions.width ||
                this.viewportDimensions.height !== viewportDimensions.height) {
                this.updateViewportSize(viewportDimensions);
                this.resetZoom();
            }
        };
        /**
         * Updates the viewport size to given dimensions
         */
        this.updateViewportSize = (viewportDimensions) => {
            this.viewportDimensions = viewportDimensions;
        };
        /**
         * Get the x and y translation of the content when centered within the viewport.
         */
        this.getCenteredContentTranslation = (scale) => {
            const contentDimensions = this.calculateContentDimensionsAtScale(scale);
            return {
                x: (this.viewportDimensions.width - contentDimensions.width) / 2,
                y: (this.viewportDimensions.height - contentDimensions.height) / 2
            };
        };
        /**
         * Gets the centre point of the viewport.
         */
        this.getViewportCenterPoint = () => centerPoint(this.viewportDimensions);
        this.touchRecognizer.onSingleTap(this.handleTap);
        this.touchRecognizer.onDoubleTap(this.handleDoubleTap);
        this.touchRecognizer.onPinch(this.handlePinch);
        this.touchRecognizer.onPan(this.panBy);
        this.mouseRecognizer.onPan(this.panBy);
    }
    /**
     * Returns whether or not the content is in the middle of a zoom or pan animation.
     */
    get isAnimating() {
        return !!(this.animator && this.animator.isAnimating);
    }
    /**
     * Returns whether or not the content of the viewport is zoomed in.
     */
    get isZoomedIn() {
        return this.getCurrentTotalScale() > this.minScale;
    }
    /**
     * Sets the viewport and content elements, registers event listeners and updates the viewport size.
     */
    setElements(viewport, content, contentDimensions) {
        this.viewport = viewport;
        this.content = content;
        this.contentDimensions = contentDimensions;
        this.updateViewportSize(this.getViewportDimensions());
        this.setEventListeners();
        // currentContentTransformation is undefined until resetZoom is initially called, but kept as non-nullable to simplify code
        if (this.currentContentTransformation === undefined) {
            this.resetZoom();
        }
    }
    /**
     * Updates the min scale and centers the content within the viewport.
     */
    resetZoom() {
        const { width, height } = this.viewportDimensions;
        const minXScale = width / this.contentDimensions.width;
        const minYScale = height / this.contentDimensions.height;
        const minScale = Math.min(minXScale, minYScale, this.maxScale);
        this.minScale = minScale;
        const transformation = {
            scale: minScale,
            translation: this.getCenteredContentTranslation(minScale)
        };
        this.applyContentTransformation(transformation, false);
    }
    /**
     * Removes all event listeners.
     */
    unmount() {
        this.removeEventListeners();
    }
    /**
     * Restricts a given scale by the minScale and maxScale values.
     */
    restrictScale(scale) {
        return clamp(this.minScale, this.maxScale)(scale);
    }
    /**
     * Gets the current dimensions of the viewport.
     */
    getViewportDimensions() {
        return {
            width: this.viewport ? this.viewport.clientWidth : 0,
            height: this.viewport ? this.viewport.clientHeight : 0
        };
    }
    /**
     * Gets the content dimensions relative to a given zoom scale.
     */
    calculateContentDimensionsAtScale(scale) {
        return {
            width: this.contentDimensions ? this.contentDimensions.width * scale : 0,
            height: this.contentDimensions ? this.contentDimensions.height * scale : 0
        };
    }
}
